<script setup>
import { CheckCircle2, Copy } from 'lucide-vue-next';
import { computed, ref, useId } from 'vue';

import AnalyticsChart from '@/components/charts/AnalyticsChart.vue';
import Tooltip from '@/components/ds/Tooltip.vue';
import { announce } from '@/composables/useAriaAnnouncer.js';

const props = defineProps({
  pageUrl: { type: String, required: true },
  selector: { type: String, default: null },
  count: { type: Number, required: true },
  spikeOnset: { type: Object, default: null }, // { iso, label }
  sparklinePoints: { type: Array, default: () => [] },
  variant: { type: String, default: 'rage', validator: (v) => ['rage', 'dead'].includes(v) },
  onClick: { type: Function, default: null },
  canDrillDown: { type: Boolean, default: true },
});

const describedById = useId();

const truncatedUrl = computed(() => {
  if (props.pageUrl.length <= 60) return props.pageUrl;
  return props.pageUrl.slice(0, 28) + '…' + props.pageUrl.slice(-29);
});

const variantLabel = computed(() => (props.variant === 'rage' ? 'incidents' : 'occurrences'));

const displaySelector = computed(() => props.selector || 'Unknown element');

const plainLanguage = computed(() => {
  const parts = [
    `Page ${props.pageUrl}`,
    displaySelector.value,
    `${props.count} ${variantLabel.value}`,
  ];
  if (props.spikeOnset?.label) parts.push(`spike started ${props.spikeOnset.label}`);
  return parts.join(', ');
});

const urlCopied = ref(false);
const selectorCopied = ref(false);

async function copyToClipboard(text, kind) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      throw new Error('clipboard unavailable');
    }
    if (kind === 'url') {
      urlCopied.value = true;
      announce('URL copied to clipboard');
      setTimeout(() => { urlCopied.value = false; }, 1200);
    } else {
      selectorCopied.value = true;
      announce('Selector copied to clipboard');
      setTimeout(() => { selectorCopied.value = false; }, 1200);
    }
  } catch {
    announce('Copy failed');
  }
}

function handleRowClick() {
  if (!props.canDrillDown) return;
  if (typeof props.onClick === 'function') props.onClick();
}

function onKeyActivate(evt) {
  if (!props.canDrillDown) return;
  if (evt.key === 'Enter' || evt.key === ' ') {
    evt.preventDefault();
    handleRowClick();
  }
}

const RowTag = computed(() => (props.canDrillDown ? 'button' : 'div'));
</script>

<template>
  <Tooltip>
    <template #trigger>
      <component
        :is="RowTag"
        :type="canDrillDown ? 'button' : undefined"
        :aria-describedby="describedById"
        :class="['hotspot', { 'hotspot--drillable': canDrillDown }]"
        @click="handleRowClick"
        @keydown="onKeyActivate"
      >
        <span class="hotspot__url">{{ truncatedUrl }}</span>
        <span class="hotspot__count numeric">{{ count }}</span>
      </component>
      <span
        :id="describedById"
        class="visually-hidden"
      >{{ plainLanguage }}</span>
    </template>
    <template #content>
      <div class="hotspot-tip">
        <div class="hotspot-tip__row">
          <code class="hotspot-tip__code">{{ pageUrl }}</code>
          <button
            type="button"
            class="hotspot-tip__copy"
            :aria-label="urlCopied ? 'URL copied' : 'Copy URL'"
            @click="copyToClipboard(pageUrl, 'url')"
          >
            <CheckCircle2
              v-if="urlCopied"
              class="hotspot-tip__copy-icon"
              aria-hidden="true"
            />
            <Copy
              v-else
              class="hotspot-tip__copy-icon"
              aria-hidden="true"
            />
          </button>
        </div>
        <div class="hotspot-tip__row">
          <code class="hotspot-tip__code">{{ displaySelector }}</code>
          <button
            v-if="selector"
            type="button"
            class="hotspot-tip__copy"
            :aria-label="selectorCopied ? 'Selector copied' : 'Copy selector'"
            @click="copyToClipboard(selector, 'selector')"
          >
            <CheckCircle2
              v-if="selectorCopied"
              class="hotspot-tip__copy-icon"
              aria-hidden="true"
            />
            <Copy
              v-else
              class="hotspot-tip__copy-icon"
              aria-hidden="true"
            />
          </button>
        </div>
        <div class="hotspot-tip__row hotspot-tip__row--spike">
          <span class="hotspot-tip__spike">
            <template v-if="spikeOnset">
              {{ spikeOnset.label }} ({{ spikeOnset.iso }})
            </template>
            <template v-else>
              No spike pattern
            </template>
          </span>
          <AnalyticsChart
            type="line"
            compact
            :labels="sparklinePoints.map((p) => p.ts)"
            :datasets="[{ label: variantLabel, data: sparklinePoints.map((p) => p.value) }]"
            :aria-label="`${variantLabel} trend`"
          />
        </div>
      </div>
    </template>
  </Tooltip>
</template>

<style scoped>
.hotspot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-3) var(--space-4);
  border: var(--bw-thin) solid var(--border-default);
  background: transparent;
  color: var(--fg-primary);
  text-align: left;
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  border-radius: var(--radius-lg);
}

.hotspot--drillable {
  cursor: pointer;
}

.hotspot--drillable:hover {
  background: var(--bg-subtle);
}

.hotspot:focus-visible {
  outline: 0;
  box-shadow: var(--shadow-focus-ring);
}

.hotspot__url {
  font-family: var(--font-mono);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.hotspot__count {
  font-weight: var(--fw-semibold);
  font-size: var(--text-base);
  color: var(--fg-primary);
}

.hotspot-tip {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 18rem;
}

.hotspot-tip__row {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.hotspot-tip__row--spike {
  justify-content: space-between;
}

.hotspot-tip__code {
  font-family: var(--font-mono);
  font-size: var(--text-xs);
  color: var(--fg-primary);
  background: var(--bg-canvas);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hotspot-tip__copy {
  background: transparent;
  border: 0;
  padding: var(--space-1);
  color: var(--fg-secondary);
  cursor: pointer;
  border-radius: var(--radius-sm);
}

.hotspot-tip__copy:hover {
  color: var(--fg-primary);
  background: var(--bg-canvas);
}

.hotspot-tip__copy:focus-visible {
  outline: 0;
  box-shadow: var(--shadow-focus-ring);
}

.hotspot-tip__copy-icon {
  width: 1em;
  height: 1em;
}

.hotspot-tip__spike {
  font-size: var(--text-xs);
  color: var(--fg-secondary);
}
</style>
